import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CartProvider } from './contexts/CartContext';

// Eager load critical routes
import PublicCmsPage from './components/cms/PublicCmsPage';
import PublicCmsRoute from './components/cms/PublicCmsRoute';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ProgressiveRegister from './pages/auth/ProgressiveRegister';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import OAuthCallback from './pages/auth/OAuthCallback';
import NotFound from './pages/NotFound';
import AcceptInvitation from './pages/invitations/AcceptInvitation';

// Lazy load authenticated pages
const ModernLayout = lazy(() => import('./components/layout/ModernLayout'));
const ModernDashboard = lazy(() => import('./pages/ModernDashboard'));
const MemberDashboard = lazy(() => import('./pages/MemberDashboard'));

// Subscription & Onboarding
const Cart = lazy(() => import('./pages/subscription/Cart'));
const CheckoutPage = lazy(() => import('./pages/subscription/CheckoutPage'));
const Onboarding = lazy(() => import('./pages/onboarding/Onboarding'));

// Chamas
const ChamaList = lazy(() => import('./pages/chamas/ChamaList'));
const CreateChama = lazy(() => import('./pages/chamas/CreateChama'));
const ChamaDetail = lazy(() => import('./pages/chamas/ChamaDetailEnhanced'));
const Members = lazy(() => import('./pages/members/Members'));
const MemberDetails = lazy(() => import('./pages/members/MemberDetails'));
const GroupIntegrationSettings = lazy(() => import('./pages/chamas/GroupIntegrationSettings'));

// Financial
const ContributionsList = lazy(() => import('./pages/contributions/ContributionsList'));
const LoansList = lazy(() => import('./pages/loans/LoansList'));
const PotsList = lazy(() => import('./pages/pots/PotsList'));
const CyclesList = lazy(() => import('./pages/cycles/CyclesList'));
const Investments = lazy(() => import('./pages/investments/Investments'));

// Management
const MeetingsList = lazy(() => import('./pages/meetings/MeetingsList'));
const Reports = lazy(() => import('./pages/Reports'));
const Welfare = lazy(() => import('./pages/Welfare'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const RolesManagement = lazy(() => import('./pages/settings/RolesManagement'));
const SubscriptionManagement = lazy(() => import('./pages/subscription/SubscriptionManagement'));
const BulkImport = lazy(() => import('./pages/tools/BulkImport'));
const Support = lazy(() => import('./pages/support/Support'));

// Other
const PublicCampaigns = lazy(() => import('./pages/campaigns/PublicCampaigns'));
const ContributionSchedules = lazy(() => import('./pages/schedules/ContributionSchedules'));
const AuditLogs = lazy(() => import('./pages/audit/AuditLogs'));

// Admin
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const SubscriptionManagementAdmin = lazy(() => import('./pages/admin/SubscriptionManagement'));
const PlanManagement = lazy(() => import('./pages/admin/PlanManagement'));
const MembershipManagement = lazy(() => import('./pages/admin/MembershipManagement'));
const PaymentReconciliation = lazy(() => import('./pages/admin/PaymentReconciliation'));
const MpesaSettings = lazy(() => import('./pages/admin/MpesaSettings'));
const PlatformIntegrations = lazy(() => import('./pages/admin/PlatformIntegrations'));
const GroupIntegrationApprovals = lazy(() => import('./pages/admin/GroupIntegrationApprovals'));
const VisitorAnalytics = lazy(() => import('./pages/admin/VisitorAnalytics'));
const VisitorDetails = lazy(() => import('./pages/admin/VisitorDetails'));
const RegisteredVisitors = lazy(() => import('./pages/admin/RegisteredVisitors'));
const BlockedIps = lazy(() => import('./pages/admin/BlockedIps'));

// CMS (Landing Page CMS)
const CmsPages = lazy(() => import('./pages/admin/CmsPages'));
const CmsPageEditor = lazy(() => import('./pages/admin/CmsPageEditor'));
const CmsNavigation = lazy(() => import('./pages/admin/CmsNavigation'));
const CmsMediaLibrary = lazy(() => import('./pages/admin/CmsMediaLibrary'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen bg-[#0f1115] flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 rounded-full border-4 border-emerald-500/20 border-t-emerald-500 animate-spin mx-auto mb-4" />
      <p className="text-[#d8d2c5] text-sm">Loading...</p>
    </div>
  </div>
);

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const SystemAdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return user?.is_system_admin ? children : <Navigate to="/dashboard" replace />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

const App = () => (
  <Router>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <CartProvider>
            <ToastProvider>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<PublicCmsPage slug="home" />} />
                  <Route path="/p/:slug" element={<PublicCmsRoute />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<ProgressiveRegister />} />
                  <Route path="/register/classic" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/auth/callback" element={<OAuthCallback />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/invitation/accept" element={<AcceptInvitation />} />

                  {/* Subscription Routes - Public */}
                  <Route path="/subscription/pricing" element={<Navigate to="/#pricing" replace />} />
                  <Route path="/subscription/cart" element={<Cart />} />
                  <Route path="/subscription/checkout" element={<CheckoutPage />} />

                  {/* Onboarding - Protected */}
                  <Route
                    path="/onboarding"
                    element={
                      <PrivateRoute>
                        <Onboarding />
                      </PrivateRoute>
                    }
                  />

                  {/* Protected Routes with Modern Layout */}
                  <Route
                    path="/*"
                    element={
                      <PrivateRoute>
                        <ModernLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route path="dashboard" element={<ModernDashboard />} />
                    <Route path="member/dashboard" element={<MemberDashboard />} />

                    {/* Chamas */}
                    <Route path="chamas" element={<ChamaList />} />
                    <Route path="chamas/create" element={<CreateChama />} />
                    <Route path="chamas/:id" element={<ChamaDetail />} />
                    <Route path="chamas/:chamaId/members" element={<Members />} />
                    <Route path="chamas/:chamaId/members/:memberId" element={<MemberDetails />} />
                    <Route
                      path="chamas/:chamaId/integration-settings"
                      element={<GroupIntegrationSettings />}
                    />

                    {/* Contributions */}
                    <Route path="contributions" element={<ContributionsList />} />

                    {/* Pots */}
                    <Route path="pots" element={<PotsList />} />

                    {/* Cycles */}
                    <Route path="cycles" element={<CyclesList />} />

                    {/* Loans */}
                    <Route path="loans" element={<LoansList />} />

                    {/* Meetings */}
                    <Route path="meetings" element={<MeetingsList />} />

                    {/* Reports */}
                    <Route path="reports" element={<Reports />} />

                    {/* Welfare */}
                    <Route path="welfare" element={<Welfare />} />

                    {/* Subscription */}
                    <Route path="subscription" element={<SubscriptionManagement />} />

                    {/* Campaigns */}
                    <Route path="campaigns" element={<PublicCampaigns />} />

                    {/* Investments */}
                    <Route path="investments" element={<Investments />} />

                    {/* Schedules */}
                    <Route path="schedules" element={<ContributionSchedules />} />

                    {/* Audit Logs */}
                    <Route path="audit" element={<AuditLogs />} />

                    {/* Settings */}
                    <Route path="settings" element={<Settings />} />
                    <Route path="settings/roles" element={<RolesManagement />} />

                    {/* Admin - Super Admin Only */}
                    <Route
                      path="admin/dashboard"
                      element={
                        <SystemAdminRoute>
                          <AdminDashboard />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/users"
                      element={
                        <SystemAdminRoute>
                          <UserManagement />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/subscriptions"
                      element={
                        <SystemAdminRoute>
                          <SubscriptionManagementAdmin />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/memberships"
                      element={
                        <SystemAdminRoute>
                          <MembershipManagement />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/plans"
                      element={
                        <SystemAdminRoute>
                          <PlanManagement />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/payments"
                      element={
                        <SystemAdminRoute>
                          <PaymentReconciliation />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/mpesa-settings"
                      element={
                        <SystemAdminRoute>
                          <MpesaSettings />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/integrations"
                      element={
                        <SystemAdminRoute>
                          <PlatformIntegrations />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/integration-approvals"
                      element={
                        <SystemAdminRoute>
                          <GroupIntegrationApprovals />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/analytics/visitors"
                      element={
                        <SystemAdminRoute>
                          <VisitorAnalytics />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/analytics/visitor/:ipAddress"
                      element={
                        <SystemAdminRoute>
                          <VisitorDetails />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/analytics/registered-visitors"
                      element={
                        <SystemAdminRoute>
                          <RegisteredVisitors />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/blocked-ips"
                      element={
                        <SystemAdminRoute>
                          <BlockedIps />
                        </SystemAdminRoute>
                      }
                    />

                    {/* Landing Page CMS */}
                    <Route
                      path="admin/cms/pages"
                      element={
                        <SystemAdminRoute>
                          <CmsPages />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/cms/pages/:pageId"
                      element={
                        <SystemAdminRoute>
                          <CmsPageEditor />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/cms/navigation"
                      element={
                        <SystemAdminRoute>
                          <CmsNavigation />
                        </SystemAdminRoute>
                      }
                    />
                    <Route
                      path="admin/cms/media"
                      element={
                        <SystemAdminRoute>
                          <CmsMediaLibrary />
                        </SystemAdminRoute>
                      }
                    />

                    {/* Tools */}
                    <Route path="tools/bulk-import" element={<BulkImport />} />

                    {/* Support */}
                    <Route path="support" element={<Support />} />
                  </Route>

                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ToastProvider>
          </CartProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  </Router>
);

export default App;
